// Wishlist entrypoint.
//
// This script is loaded on wishlist pages.
//
// the dialog module exposes itself globally as window.Dialog; we reference it
// here rather than via a bare es import because this file is bundled (gulp-include
// + terser) and served as a single module without an import map to resolve "dialog".
const Dialog = window.Dialog;

const delegateEvent = (root, eventName, selector, handler) => {
    root.addEventListener(eventName, e => {
        const target = e.target?.closest(selector);
        if (!target || !root.contains(target)) {
            return;
        }

        handler(e, target);
    });
};

const getWishlistId = elm => {
    const wishlist = elm || document.querySelector('.wishlist');

    if (!wishlist || !wishlist.dataset.wishlistUuid) {
        return null;
    }

    return wishlist.dataset.wishlistUuid;
};

const wishlistURL = uuid =>
    new URL(`/wishlists${uuid ? `/${uuid}` : ''}`, window.location.origin);

const updateWishlist = async (uuid, name) => {
    const response = await fetch(`/wishlists/${uuid}`, {
        method: 'PUT',
        headers: {
            'Content-type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
            wishlist_name: name,
        }),
        credentials: 'include',
        redirect: 'error',
    });

    const body = await response.json();

    if (!response.ok) {
        throw new Error('Failed to update wishlist', {
            cause: {
                status: response.status,
                ...body,
            },
        });
    }

    return body;
};

const shareWishlist = async uuid => {
    const url = new URL(`/wishlists/${uuid}/share`, window.location.origin);
    url.searchParams.set('ajax', '1');

    const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        redirect: 'error',
    });

    const body = await response.json();

    if (!response.ok) {
        throw new Error('Failed to share wishlist', {
            cause: {
                status: response.status,
                ...body,
            },
        });
    }

    if (!body.payload?.html) {
        return;
    }

    const shareDialog = document.querySelector('#wishlist-share');
    if (!(shareDialog instanceof HTMLElement)) {
        return;
    }

    const content =
        shareDialog.querySelector('.dialog__content') ?? shareDialog;
    content.innerHTML = body.payload.html;

    Dialog.render('#wishlist-share', {
        modifiers: ['wishlist', 'sidebar'],
        callback: function () {
            const btn = this.content.querySelector('.js-copy-text');

            if (!(btn instanceof HTMLElement)) {
                return;
            }

            btn.addEventListener('click', e => {
                e.preventDefault();

                const link = e.target
                    .closest('.wishlist-dialog__form-row')
                    ?.querySelector('input')?.value;

                if (!link) {
                    return;
                }

                navigator.clipboard.writeText(link);

                const textElm = btn.querySelector('span');
                if (textElm) {
                    textElm.innerText = 'Copied';
                }
            });
        },
    });
};

const errorAlert = (message, duration = 3000) => {
    const alertBox = document.createElement('div');
    Object.assign(alertBox.style, {
        position: 'fixed',
        top: '1rem',
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'var(--wishlist-error-color, #B53638)',
        color: 'var(--wishlist-button-text-color, #FFFFFF)',
        padding: '1em',
        zIndex: 2147483647,
    });
    alertBox.textContent = message;
    document.body.appendChild(alertBox);
    setTimeout(() => alertBox.remove(), duration);
};

const getWishlistElements = () => {
    const wishlist = document.querySelector('.wishlist');

    return {
        wishlist,
        wishlistBanner: wishlist?.querySelector('.wishlist-banner') || null,
        wishlistTitle:
            wishlist?.querySelector('.wishlist-banner__title') || null,
    };
};

const mediaQuery = window.matchMedia('(max-width: 750px)');

mediaQuery.addEventListener('change', e => {
    if (!e.matches) {
        const current = Dialog?.current;
        if (current?.opened && current.target?.id?.indexOf('mobile') > -1) {
            Dialog.close();
        }
    }
});

delegateEvent(document, 'click', `.js-wishlist-banner-edit`, e => {
    const { wishlistBanner, wishlistTitle } = getWishlistElements();
    if (!wishlistBanner || !wishlistTitle) {
        return;
    }

    wishlistBanner.dataset.isEditable = true;
    wishlistTitle.setAttribute('contenteditable', true);
    wishlistTitle.focus();
});

delegateEvent(document, 'click', `.js-share-wishlist`, async e => {
    await shareWishlist(getWishlistId());
});

delegateEvent(document, 'click', `.js-wishlist-add-all`, async (e, target) => {
    e.preventDefault();
    e.stopPropagation();

    const url = new URL(target.href, window.location.origin);
    url.searchParams.set('ajax', '1');

    try {
        const response = await fetch(url, {
            method: 'GET',
            credentials: 'include',
            redirect: 'error',
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
            },
        });

        if (!response.ok) {
            throw new Error('Failed to add all items to basket', {
                cause: {
                    status: response.status,
                },
            });
        }

        const body = await response.json();

        if (!body.result || !body.payload) {
            throw new Error('Invalid response from add-all endpoint');
        }

        const count = body.payload.count ?? body.payload.basketCount;
        const basketCount = document.querySelector(
            '.js-basket-count .header-action__count'
        );
        if (basketCount && count !== undefined) {
            basketCount.innerHTML = count;
        }

        if (window.Soho?.flyout) {
            window.Soho.flyout.load({ scrollToTop: true });
        } else if (body.payload.html) {
            // Fallback for non basket-v2 sites using the old quick-basket popup.
            const quickBasketWrap = document.querySelector('.js-quick-basket__wrap');
            if (quickBasketWrap) {
                quickBasketWrap.dispatchEvent(new CustomEvent('wishlist.add-all', {
                    detail: {
                        basketCount: count,
                        popupHtml: body.payload.html,
                    },
                }));
            } else if (body.payload.basketUrl) {
                window.location.href = body.payload.basketUrl;
            }
        } else if (body.payload.basketUrl) {
            window.location.href = body.payload.basketUrl;
        }
    } catch (err) {
        console.error('Failed to add all items to basket', err);
    }
});

delegateEvent(document, 'click', `.js-wishlist-banner-update`, async e => {
    const { wishlistBanner, wishlistTitle } = getWishlistElements();
    if (!wishlistBanner || !wishlistTitle) {
        return;
    }

    const title = wishlistBanner.querySelector('.wishlist-banner__title');
    const original = wishlistBanner.querySelector(
        'input[name="wishlist-name"]'
    );

    if (!title || !original) {
        return;
    }

    delete wishlistBanner.dataset.isEditable;
    wishlistTitle.removeAttribute('contenteditable');

    const listName = title.textContent;

    try {
        await updateWishlist(getWishlistId(), listName);
    } catch (e) {
        console.error('Failed to update wishlist', e);
        errorAlert('Failed to update wishlist. Please try again.');

        title.textContent = original.value;
        return;
    }

    //update input
    original.value = title.textContent;

    // update inner option label
    const optionElm = wishlistBanner.querySelector(
        `a[href="${wishlistURL(getWishlistId()).pathname}"] p`
    );
    if (optionElm) {
        const count = optionElm.querySelector('span');
        optionElm.textContent = title.textContent + ' ';

        if (count) {
            optionElm.appendChild(count);
        }
    }
});

const updateEmbeddedWishlistItem = (product, option) => {
    const key = option.dataset.key;
    const sku = option.dataset.sku;

    if (!key) {
        return;
    }

    product.querySelectorAll('.colour-selection .swatch--active').forEach(active => {
        active.classList.remove('swatch--active');
    });
    option.classList.add('swatch--active');

    const wishlistButton = product.querySelector('.js-wishlist-add');
    if (wishlistButton) {
        wishlistButton.dataset.key = key;
        if (sku) {
            wishlistButton.dataset.sku = sku;
        }
    }

    const productLink = product.querySelector('a.product__link[data-sku]');
    if (productLink && sku) {
        productLink.dataset.sku = sku;
    }

    const removeLink = product.querySelector('.listing-item__remove[href]');
    if (removeLink) {
        const url = new URL(removeLink.href, window.location.origin);
        url.searchParams.set('key', key);
        removeLink.href = `${url.pathname}${url.search}${url.hash}`;
    }
};

const initEmbeddedWishlistListings = () => {
    document.querySelectorAll('.listing--embedded').forEach(listing => {
        if (listing.dataset.embeddedWishlistInitialised) {
            return;
        }

        listing.dataset.embeddedWishlistInitialised = 'true';
        listing.addEventListener('click', event => {
            const option = event.target.closest('.colour-selection [data-key]');

            if (!option || !listing.contains(option) || !option.dataset.key) {
                return;
            }

            const product = option.closest('.product');
            if (!product) {
                return;
            }

            updateEmbeddedWishlistItem(product, option);

            if (option.dataset.href) {
                window.setTimeout(() => {
                    if (!event.defaultPrevented) {
                        window.location.assign(option.dataset.href);
                    }
                }, 0);
                return;
            }

            event.preventDefault();
            event.stopPropagation();
        });
    });
};

const wishlistListingSnapshotPointerKey = 'sohoWishlistListingSnapshotKey';

const isBackForwardNavigation = () => {
    const navigation = window.performance.getEntriesByType?.('navigation')?.[0];

    if (navigation) {
        return navigation.type === 'back_forward';
    }

    return window.performance.navigation?.type
        === window.performance.navigation?.TYPE_BACK_FORWARD;
};

const initWishlistLoadMore = root => {
    if (root.dataset.wishlistListingInitialised) {
        return;
    }

    const listingItems = root.querySelector('#wishlist-listing-items');
    const listingPagination = root.querySelector('#wishlist-listing-pagination');
    const previousPagination = root.querySelector('.js-listing-pagination-previous');
    const previousLink = root.querySelector('.js-listing-view-previous');

    if (!listingItems || !listingPagination || !previousPagination || !previousLink) {
        return;
    }

    root.dataset.wishlistListingInitialised = 'true';

    const pageStates = new Map();
    let loadedStartRow = Number(root.dataset.startRow);
    let loadedEndRow = Number(root.dataset.endRow);
    let pageRequest = null;

    const getLoadedPages = () => Array.from(pageStates.keys())
        .map(Number)
        .filter(Boolean)
        .sort((a, b) => a - b);

    const getPageUrl = (page, sourceUrl = window.location.href) => {
        const url = new URL(sourceUrl, window.location.origin);

        url.searchParams.delete('ajax');
        if (Number(page) > 1) {
            url.searchParams.set('page', page);
        } else {
            url.searchParams.delete('page');
        }

        return url.href;
    };

    const rememberPageState = (page, state) => {
        page = Number(page);

        if (page && state?.body && state?.data) {
            pageStates.set(page, state);
        }
    };

    const markProductLinks = (container, page) => {
        page = Number(page);

        if (!page) {
            return;
        }

        container.querySelectorAll('a.product__link[data-sku]').forEach(link => {
            link.dataset.listingPage = page;
        });
    };

    const syncViewPrevious = () => {
        const firstPage = getLoadedPages()[0];
        const firstPageState = pageStates.get(firstPage);
        const hasPreviousPage = loadedStartRow > 1 && firstPage > 1 && firstPageState?.url;

        previousPagination.hidden = !hasPreviousPage;

        if (hasPreviousPage) {
            previousLink.href = getPageUrl(firstPage - 1, firstPageState.url);
        }
    };

    const getHistoryState = () => window.history.state?.wishlistListing;

    const replaceHistoryState = (pageState, url, accumulated = {}) => {
        const currentState = getHistoryState();
        const state = Object.assign({}, window.history.state, {
            wishlistListing: {
                pageState,
                accumulated: {
                    startRow: loadedStartRow,
                    endRow: loadedEndRow,
                    page: Number(pageState.data.page),
                    limit: Number(pageState.data.limit),
                    ...accumulated,
                },
                anchor: currentState?.anchor || null,
            },
        });

        window.history.replaceState(state, null, url);
    };

    const initialPage = Number(root.dataset.page) || 1;
    const initialPageState = {
        url: getPageUrl(initialPage),
        body: listingItems.innerHTML,
        pagination: listingPagination.innerHTML,
        data: {
            totalResults: Number(root.dataset.totalResults),
            startRow: loadedStartRow,
            endRow: loadedEndRow,
            page: initialPage,
            limit: Number(root.dataset.limit),
        },
    };

    rememberPageState(initialPage, initialPageState);
    markProductLinks(listingItems, initialPage);

    if (!getHistoryState()?.anchor) {
        replaceHistoryState(initialPageState, initialPageState.url);
    }

    syncViewPrevious();

    const parseListingBody = body => {
        const template = document.createElement('template');
        template.innerHTML = body;

        return template.content.querySelector('#wishlist-listing-items');
    };

    const fetchPage = async url => {
        const requestUrl = new URL(url, window.location.origin);
        requestUrl.searchParams.set('ajax', '1');

        const response = await fetch(requestUrl, {
            method: 'GET',
            credentials: 'include',
            redirect: 'error',
            headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
        });
        const responseBody = await response.json();

        if (!response.ok || !responseBody.result || !responseBody.payload) {
            throw new Error('Invalid wishlist listing response', {
                cause: { status: response.status },
            });
        }

        return {
            url: requestUrl.href,
            ...responseBody.payload,
        };
    };

    const setLoading = loading => {
        root.dataset.loading = loading ? 'true' : 'false';
        root.setAttribute('aria-busy', loading ? 'true' : 'false');
    };

    root.addEventListener('click', async event => {
        const viewPrevious = event.target.closest('.js-listing-view-previous');
        const viewMore = event.target.closest('.js-listing-view-more');
        const link = viewPrevious || viewMore;

        if (!link || !root.contains(link)) {
            return;
        }

        event.preventDefault();

        if (pageRequest || root.dataset.loading === 'true') {
            return;
        }

        const existingItems = Array.from(listingItems.children);
        const mouseAnchor = viewPrevious && event.detail !== 0
            ? listingItems.firstElementChild
            : null;

        pageRequest = {
            direction: viewPrevious ? 'previous' : 'next',
            focusNewItems: event.detail === 0,
            pagination: listingPagination.innerHTML,
            mouseAnchor,
            mouseAnchorTop: mouseAnchor?.getBoundingClientRect().top,
        };
        setLoading(true);

        try {
            const response = await fetchPage(link.href);
            const responseItems = parseListingBody(response.body);

            if (!responseItems || !response.data) {
                throw new Error('Wishlist listing response is missing its items');
            }

            const page = Number(response.data.page);
            const pageState = {
                url: getPageUrl(page, response.url),
                body: responseItems.innerHTML,
                pagination: response.pagination || '',
                data: response.data,
            };
            const newItems = Array.from(responseItems.children);

            markProductLinks(responseItems, page);
            rememberPageState(page, pageState);
            listingItems.replaceChildren(...newItems);

            const firstNewProductLink = listingItems.querySelector(
                'a.product__link[data-sku]'
            );

            if (pageRequest.direction === 'previous') {
                listingItems.append(...existingItems);
                listingPagination.innerHTML = pageRequest.pagination;
                loadedStartRow = Number(response.data.startRow);

                const currentAnchorTop = pageRequest.mouseAnchor
                    ?.getBoundingClientRect().top;
                if (
                    Number.isFinite(currentAnchorTop)
                    && Number.isFinite(pageRequest.mouseAnchorTop)
                ) {
                    window.scrollBy(0, currentAnchorTop - pageRequest.mouseAnchorTop);
                }
            } else {
                listingItems.prepend(...existingItems);
                listingPagination.innerHTML = response.pagination || '';
                loadedEndRow = Number(response.data.endRow);
            }

            root.dataset.startRow = loadedStartRow;
            root.dataset.endRow = loadedEndRow;
            root.dataset.page = page;

            replaceHistoryState(pageState, pageState.url);
            syncViewPrevious();

            if (pageRequest.focusNewItems && firstNewProductLink) {
                firstNewProductLink.focus({ preventScroll: true });
            }

            if (typeof window.D3R?.InitHotspots === 'function') {
                window.D3R.InitHotspots();
            }
            if (typeof window.Soho?.GTM?.recordProductImpressions === 'function') {
                window.Soho.GTM.recordProductImpressions(response.data.startRow);
            }
        } catch (error) {
            console.error('Failed to load wishlist items', error);
            errorAlert('Failed to load wishlist items. Please try again.');
        } finally {
            pageRequest = null;
            setLoading(false);
        }
    });

    const storeListingSnapshot = () => {
        const key = `sohoWishlistListingSnapshot:${Date.now()}:${Math.random()
            .toString(36).slice(2)}`;
        const snapshot = JSON.stringify({
            version: 1,
            url: window.location.href,
            pages: getLoadedPages().map(page => ({
                page,
                state: pageStates.get(page),
            })),
            pagination: listingPagination.innerHTML,
            startRow: loadedStartRow,
            endRow: loadedEndRow,
        });

        try {
            const previousKey = window.sessionStorage.getItem(
                wishlistListingSnapshotPointerKey
            );

            if (previousKey) {
                window.sessionStorage.removeItem(previousKey);
            }

            window.sessionStorage.setItem(key, snapshot);
            window.sessionStorage.setItem(wishlistListingSnapshotPointerKey, key);

            return key;
        } catch (error) {
            try {
                window.sessionStorage.removeItem(key);
            } catch (storageError) {
                // The page-only fallback below does not depend on web storage.
            }

            return null;
        }
    };

    listingItems.addEventListener('click', event => {
        const link = event.target.closest('a.product__link[data-sku]');
        const currentState = getHistoryState();

        if (!link || !currentState?.pageState) {
            return;
        }

        const sourcePage = Number(link.dataset.listingPage)
            || Number(currentState.pageState.data.page);
        const latestPage = Number(currentState.accumulated?.page)
            || Number(currentState.pageState.data.page);
        const snapshotKey = storeListingSnapshot();
        const productLinks = Array.from(
            listingItems.querySelectorAll('a.product__link[data-sku]')
        );
        const anchor = {
            sku: link.dataset.sku,
            href: link.href,
            productIndex: productLinks.indexOf(link),
            sourcePage,
            latestPage,
            latestUrl: window.location.href,
            viewportTop: link.getBoundingClientRect().top,
            scrollY: window.scrollY,
            snapshotKey,
            pageOnlyFallback: false,
        };
        let pageState = currentState.pageState;
        let stateUrl = window.location.href;

        if (!snapshotKey && sourcePage !== latestPage && pageStates.has(sourcePage)) {
            pageState = pageStates.get(sourcePage);
            stateUrl = getPageUrl(sourcePage, pageState.url);
            anchor.pageOnlyFallback = true;
        }

        window.history.replaceState(Object.assign({}, window.history.state, {
            wishlistListing: {
                pageState,
                accumulated: currentState.accumulated,
                anchor,
            },
        }), null, stateUrl);
    }, true);

    const restoreListingSnapshot = anchor => {
        if (!anchor.snapshotKey) {
            return false;
        }

        try {
            const storedSnapshot = window.sessionStorage.getItem(anchor.snapshotKey);
            const snapshot = storedSnapshot ? JSON.parse(storedSnapshot) : null;

            if (
                !snapshot
                || snapshot.version !== 1
                || snapshot.url !== anchor.latestUrl
                || !Array.isArray(snapshot.pages)
                || snapshot.pages.length === 0
            ) {
                return false;
            }

            const restoredItems = document.createDocumentFragment();
            pageStates.clear();

            snapshot.pages.forEach(pageSnapshot => {
                const template = document.createElement('template');
                template.innerHTML = pageSnapshot.state.body;
                markProductLinks(template.content, pageSnapshot.page);
                restoredItems.append(template.content);
                rememberPageState(pageSnapshot.page, pageSnapshot.state);
            });

            listingItems.replaceChildren(restoredItems);
            listingPagination.innerHTML = snapshot.pagination;
            loadedStartRow = Number(snapshot.startRow);
            loadedEndRow = Number(snapshot.endRow);
            root.dataset.startRow = loadedStartRow;
            root.dataset.endRow = loadedEndRow;

            if (typeof window.D3R?.InitHotspots === 'function') {
                window.D3R.InitHotspots();
            }

            return true;
        } catch (error) {
            return false;
        }
    };

    const restoreBoundedPageState = () => {
        loadedStartRow = Number(root.dataset.startRow);
        loadedEndRow = Number(root.dataset.endRow);
        pageStates.clear();
        rememberPageState(initialPage, initialPageState);
        markProductLinks(listingItems, initialPage);
    };

    const restoreLatestBfcacheState = anchor => {
        if (!anchor.pageOnlyFallback || !anchor.latestUrl || !pageStates.has(anchor.latestPage)) {
            return;
        }

        const latestPageState = pageStates.get(anchor.latestPage);
        window.history.replaceState(Object.assign({}, window.history.state, {
            wishlistListing: {
                pageState: latestPageState,
                accumulated: getHistoryState()?.accumulated,
                anchor: {
                    ...anchor,
                    pageOnlyFallback: false,
                },
            },
        }), null, anchor.latestUrl);
    };

    const findAnchorLink = anchor => {
        const links = Array.from(
            listingItems.querySelectorAll('a.product__link[data-sku]')
        );
        const indexedLink = links[anchor.productIndex];

        if (
            indexedLink
            && indexedLink.dataset.sku === anchor.sku
            && indexedLink.href === anchor.href
        ) {
            return indexedLink;
        }

        return links.find(link => (
            link.dataset.sku === anchor.sku
            && link.href === anchor.href
        ));
    };

    const restoreAnchorPosition = anchor => {
        const alignAnchor = () => {
            const link = findAnchorLink(anchor);

            if (!link) {
                return;
            }

            const viewportTop = Number(anchor.viewportTop);

            if (Number.isFinite(viewportTop)) {
                const targetScrollY = window.scrollY
                    + link.getBoundingClientRect().top
                    - viewportTop;
                window.scrollTo(0, Math.max(0, targetScrollY));
            } else {
                link.scrollIntoView({ block: 'center' });
            }
        };

        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                alignAnchor();
                window.requestAnimationFrame(alignAnchor);
                window.setTimeout(alignAnchor, 250);
            });
        });
    };

    window.addEventListener('pageshow', event => {
        const anchor = getHistoryState()?.anchor;

        if (!anchor || (!event.persisted && !isBackForwardNavigation())) {
            return;
        }

        if (event.persisted) {
            restoreLatestBfcacheState(anchor);
        } else if (!restoreListingSnapshot(anchor)) {
            restoreBoundedPageState();
        }

        syncViewPrevious();
        restoreAnchorPosition(anchor);
    });
};

const initWishlistLoadMoreListings = () => {
    document.querySelectorAll('.js-wishlist-listing').forEach(initWishlistLoadMore);
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        initEmbeddedWishlistListings();
        initWishlistLoadMoreListings();
    });
} else {
    initEmbeddedWishlistListings();
    initWishlistLoadMoreListings();
}
